import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';


interface CableMT1220DetailProps {
  t: any;
  isRTL: boolean;
}

const WhiteDownArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7,10 12,15 17,10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const ProductDetailPage = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding-top: 120px;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Breadcrumbs = styled(motion.div)<{ $isRTL: boolean }>`
  padding: 1rem 0;
  font-size: 0.9rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const BreadcrumbLink = styled.span<{ $isLast: boolean }>`
  color: ${props => props.$isLast ? '#dc2626' : '#64748b'};
  text-decoration: none;
  font-weight: ${props => props.$isLast ? '600' : '400'};
  cursor: ${props => props.$isLast ? 'default' : 'pointer'};
  transition: color 0.3s ease;
  
  &:hover {
    color: ${props => props.$isLast ? '#dc2626' : '#dc2626'};
  }
`;

const HeroSection = styled.div`
  background: white;
  border-radius: 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 4rem;
  position: relative;
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.05) 0%, rgba(239, 68, 68, 0.02) 100%);
  z-index: 0;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  padding: 3rem;
`;

const ProductLayout = styled.div`
  display: flex;
  gap: 4rem;
  align-items: flex-start;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 3rem;
  }
`;

const ProductImageSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const MainImage = styled.div`
  width: 100%;
  height: 500px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 20px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  border: 1px solid rgba(220, 38, 38, 0.1);
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(220, 38, 38, 0.05) 0%, rgba(239, 68, 68, 0.05) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::after {
    opacity: 1;
  }
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
    filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.1));
  }
  
  &:hover img {
    transform: scale(1.02);
  }
`;

const ProductInfoSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
`;

const ProductTitle = styled.h1<{ $isRTL: boolean }>`
  font-size: 3rem;
  font-weight: 800;
  color: #1f2937;
  margin: 0;
  line-height: 1.2;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const ProductSubtitle = styled.p<{ $isRTL: boolean }>`
  font-size: 1.3rem;
  color: #64748b;
  margin: 0;
  font-weight: 500;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const ProductDescription = styled.div<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  padding: 2.5rem;
  border: 1px solid rgba(220, 38, 38, 0.1);
  
  p {
    color: #374151;
    line-height: 1.7;
    margin: 0 0 1.5rem 0;
    font-size: 1.1rem;
    font-family: ${props => props.$isRTL ? 
      "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
      "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const DatasheetSection = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  padding: 2.5rem;
  border: 1px solid rgba(220, 38, 38, 0.1);
`;

const DatasheetTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 2rem 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #dc2626, #ef4444);
    border-radius: 2px;
  }
`;

const DatasheetButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
  color: white;
  padding: 1.25rem 2rem;
  border-radius: 16px;
  text-decoration: none;
  font-weight: 700;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(220, 38, 38, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(220, 38, 38, 0.4);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(-2px);
  }
`;

const ProductsSection = styled.div`
  margin-top: 4rem;
`;

const SectionTitle = styled.h2<{ $isRTL: boolean }>`
  font-size: 2.5rem;
  font-weight: 800;
  color: #1f2937;
  margin: 0 0 3rem 0;
  text-align: center;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ProductCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  transition: all 0.4s ease;
  border: 1px solid rgba(220, 38, 38, 0.1);
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    border-color: rgba(220, 38, 38, 0.3);
    
    &::before {
      opacity: 1;
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #dc2626, #ef4444);
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1;
  }
`;

const ProductName = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 1.5rem 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const ProductDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const DetailItem = styled.div<{ $isRTL: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  border: 1px solid rgba(220, 38, 38, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
    border-color: rgba(220, 38, 38, 0.2);
  }
`;

const DetailLabel = styled.span<{ $isRTL: boolean }>`
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 600;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const DetailValue = styled.span<{ $isRTL: boolean }>`
  font-size: 1rem;
  color: #1f2937;
  font-weight: 700;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const ProductActions = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PDFButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(220, 38, 38, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(220, 38, 38, 0.4);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const CharacteristicsSection = styled.div`
  margin-top: 4rem;
  padding-bottom: 4rem;
`;

const CharacteristicItem = styled.div<{ $isRTL: boolean }>`
  background: white;
  border-radius: 20px;
  margin-bottom: 1.5rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  border: 1px solid rgba(220, 38, 38, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
    border-color: rgba(220, 38, 38, 0.2);
  }
`;

const CharacteristicHeader = styled.div<{ $isRTL: boolean }>`
  padding: 2rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid rgba(220, 38, 38, 0.1);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  }
`;

const CharacteristicTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const ChevronIcon = styled.span<{ $isOpen: boolean }>`
  font-size: 1rem;
  color: #dc2626;
  transition: transform 0.3s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  font-weight: bold;
`;

const CharacteristicContent = styled.div<{ $isOpen: boolean }>`
  max-height: ${props => props.$isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.4s ease;
`;

const CharacteristicGrid = styled.div`
  padding: 2.5rem;
  display: grid;
  gap: 1.5rem;
`;

const CharacteristicRow = styled.div<{ $isRTL: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  border: 1px solid rgba(220, 38, 38, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
    border-color: rgba(220, 38, 38, 0.2);
  }
`;

const CharacteristicLabel = styled.span<{ $isRTL: boolean }>`
  font-weight: 600;
  color: #374151;
  font-size: 1rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const CharacteristicValue = styled.span<{ $isRTL: boolean }>`
  font-weight: 700;
  color: #1f2937;
  font-size: 1rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const CableMT1220Detail: React.FC<CableMT1220DetailProps> = ({ t, isRTL }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [openCharacteristics, setOpenCharacteristics] = useState<{ [key: string]: boolean }>({});

  const breadcrumbs = [
    { label: "Catalogue produits", path: "/" },
    { label: "Transport et distribution de l'énergie", path: "/category/cables-transport" },
    { label: t.mediumVoltageCablesTitle, path: "/category/cables-transport" },
    { label: "Cable MT NFC 33-226 12/20 (24) kV", path: "/product/cable-mt-1220" },
  ];

  const product = {
    id: 'cable-mt-1220',
    title: 'Cable MT NFC 33-226 12/20 (24) kV',
    subtitle: 'Câbles HTA 12/20 (24) kV cuivre ou aluminium',
    image: '/images/catalog/Câbles de transport et de distribution de l\'énergie/Cable MT NFC 33-226 1220 (24) kV.jpg',
    datasheet: '/images/catalog/Câbles de transport et de distribution de l\'énergie/Cable MT NFC 33-226 1220 (24) kV/Cable MT NFC 33-226 1220 (24) kV.pdf',
  };

  const products = [
    {
      id: 'nf-c-33-226-1x95-al',
      name: 'NFC33-226 12/20KV 1x95 AL',
      reference: 'Réf. Nexans 57270195400',
      conditionnement: 'Touret (m)',
      nature: 'Aluminium',
      section: '95 mm²',
      conducteurs: '1',
      resistance: '0,32 Ohm/km',
      voltageDrop: '0,34 V/A.km',
      filename: 'NFC33-226 1220KV 1x95 AL.pdf'
    },
    {
      id: 'nf-c-33-226-1x150-al',
      name: 'NFC33-226 12/20KV 1x150 AL',
      reference: 'Réf. Nexans 57270150400',
      conditionnement: 'Touret (m)',
      nature: 'Aluminium',
      section: '150 mm²',
      conducteurs: '1',
      resistance: '0,20 Ohm/km',
      voltageDrop: '0,20 V/A.km',
      filename: 'NFC 33226 1220 1x150 AL.pdf'
    },
    {
      id: 'nf-c-33-226-1x240-al',
      name: 'NFC33-226 12/20KV 1x240 AL',
      reference: 'Réf. Nexans 57270240400',
      conditionnement: 'Touret (m)',
      nature: 'Aluminium',
      section: '240 mm²',
      conducteurs: '1',
      resistance: '0,13 Ohm/km',
      voltageDrop: '0,13 V/A.km',
      filename: 'NFC 33226 1220KV 1x240 AL.pdf'
    },
    {
      id: 'nf-c-33-226-1x400-al',
      name: 'NFC33-226 12/20KV 1x400 AL',
      reference: 'Réf. Nexans 57270400400',
      conditionnement: 'Touret (m)',
      nature: 'Aluminium',
      section: '400 mm²',
      conducteurs: '1',
      resistance: '0,08 Ohm/km',
      voltageDrop: '0,08 V/A.km',
      filename: 'NFC33-226 1220KV 1x400 AL C2.pdf'
    },
    {
      id: 'nf-c-33-226-1x630-al',
      name: 'NFC33-226 12/20KV 1x630 AL',
      reference: 'Réf. Nexans 57270630400',
      conditionnement: 'Touret (m)',
      nature: 'Aluminium',
      section: '630 mm²',
      conducteurs: '1',
      resistance: '0,05 Ohm/km',
      voltageDrop: '0,05 V/A.km',
      filename: 'NFC33-226 1220KV 1x630 AL C2.pdf'
    },
    {
      id: 'nf-c-33-226-1x70-cu',
      name: 'NFC33-226 12/20KV 1x70 CU',
      reference: 'Réf. Nexans 57270070400',
      conditionnement: 'Touret (m)',
      nature: 'Cuivre',
      section: '70 mm²',
      conducteurs: '1',
      resistance: '0,26 Ohm/km',
      voltageDrop: '0,26 V/A.km',
      filename: 'NFC33-226 1220KV 1x70 CU C2.pdf'
    },
    {
      id: 'nf-c-33-226-1x185-cu',
      name: 'NFC33-226 12/20KV 1x185 CU',
      reference: 'Réf. Nexans 57270185400',
      conditionnement: 'Touret (m)',
      nature: 'Cuivre',
      section: '185 mm²',
      conducteurs: '1',
      resistance: '0,10 Ohm/km',
      voltageDrop: '0,10 V/A.km',
      filename: 'NFC33-226 1220KV 1x185 CU C2.pdf'
    },
    {
      id: 'nf-c-33-226-1x240-cu',
      name: 'NFC33-226 12/20KV 1x240 CU',
      reference: 'Réf. Nexans 57270240400',
      conditionnement: 'Touret (m)',
      nature: 'Cuivre',
      section: '240 mm²',
      conducteurs: '1',
      resistance: '0,08 Ohm/km',
      voltageDrop: '0,08 V/A.km',
      filename: 'NFC33-226 1220KV 1x240 CU C2.pdf'
    },
    {
      id: 'nf-c-33-226-3x95-al',
      name: 'NFC33-226 12/20KV 3x95 AL',
      reference: 'Réf. Nexans 57270395400',
      conditionnement: 'Touret (m)',
      nature: 'Aluminium',
      section: '95 mm²',
      conducteurs: '3',
      resistance: '0,32 Ohm/km',
      voltageDrop: '0,34 V/A.km',
      filename: 'NFC33-226 1220KV 3x95 AL.pdf'
    },
    {
      id: 'nf-c-33-226-3x150-al',
      name: 'NFC33-226 12/20KV 3x150 AL',
      reference: 'Réf. Nexans 57270350400',
      conditionnement: 'Touret (m)',
      nature: 'Aluminium',
      section: '150 mm²',
      conducteurs: '3',
      resistance: '0,20 Ohm/km',
      voltageDrop: '0,20 V/A.km',
      filename: 'NFC33-226 1220KV 3x150 AL.pdf'
    },
    {
      id: 'nf-c-33-226-3x240-al',
      name: 'NFC33-226 12/20KV 3x240 AL',
      reference: 'Réf. Nexans 57270340400',
      conditionnement: 'Touret (m)',
      nature: 'Aluminium',
      section: '240 mm²',
      conducteurs: '3',
      resistance: '0,13 Ohm/km',
      voltageDrop: '0,13 V/A.km',
      filename: 'NFC 33226 1220 3x240 AL C2.pdf'
    }
  ];

  const toggleCharacteristic = (key: string) => {
    setOpenCharacteristics(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <ProductDetailPage $isRTL={isRTL}>
      <Container>
        <Breadcrumbs $isRTL={isRTL}>
          {breadcrumbs.map((breadcrumb, index) => (
            <React.Fragment key={index}>
              <BreadcrumbLink
                $isLast={index === breadcrumbs.length - 1}
                onClick={() => index < breadcrumbs.length - 1 && navigate(breadcrumb.path)}
              >
                {breadcrumb.label}
              </BreadcrumbLink>
              {index < breadcrumbs.length - 1 && <span>›</span>}
            </React.Fragment>
          ))}
        </Breadcrumbs>

        <HeroSection>
          <HeroBackground />
          <HeroContent>
            <ProductLayout>
              <ProductImageSection>
                <MainImage>
                  <img src={product.image} alt={product.title} />
                </MainImage>
              </ProductImageSection>

              <ProductInfoSection>
                <ProductTitle $isRTL={isRTL}>{product.title}</ProductTitle>
                <ProductSubtitle $isRTL={isRTL}>{product.subtitle}</ProductSubtitle>
                
                <ProductDescription $isRTL={isRTL}>
                  <p>
                    Les câbles MT NFC 33-226 12/20 (24) kV sont des câbles de moyenne tension 
                    conformes à la norme française NFC 33-226. Ils sont conçus pour la distribution 
                    d'énergie électrique en réseau souterrain à une tension nominale de 12/20 kV.
                  </p>
                  <p>
                    Ces câbles sont disponibles en conducteurs aluminium ou cuivre, avec différentes 
                    sections et configurations (1 ou 3 conducteurs). Ils offrent une excellente 
                    résistance aux intempéries et une longue durée de vie.
                  </p>
                </ProductDescription>

                <DatasheetSection>
                  <DatasheetTitle $isRTL={isRTL}>Documentation technique</DatasheetTitle>
                  <DatasheetButton href={product.datasheet} target="_blank" rel="noopener noreferrer">
                    <WhiteDownArrow /> Télécharger la fiche technique
                  </DatasheetButton>
                </DatasheetSection>
              </ProductInfoSection>
            </ProductLayout>
          </HeroContent>
        </HeroSection>

        <ProductsSection ref={ref}>
          <SectionTitle $isRTL={isRTL}>Produits disponibles</SectionTitle>
          <ProductsGrid>
            {products.map((product, index) => (
              <ProductCard
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <ProductName $isRTL={isRTL}>{product.name}</ProductName>
                
                <ProductDetails>
                  <DetailItem $isRTL={isRTL}>
                    <DetailLabel $isRTL={isRTL}>Référence</DetailLabel>
                    <DetailValue $isRTL={isRTL}>{product.reference}</DetailValue>
                  </DetailItem>
                  
                  <DetailItem $isRTL={isRTL}>
                    <DetailLabel $isRTL={isRTL}>Nature</DetailLabel>
                    <DetailValue $isRTL={isRTL}>{product.nature}</DetailValue>
                  </DetailItem>
                  
                  <DetailItem $isRTL={isRTL}>
                    <DetailLabel $isRTL={isRTL}>Section</DetailLabel>
                    <DetailValue $isRTL={isRTL}>{product.section}</DetailValue>
                  </DetailItem>
                  
                  <DetailItem $isRTL={isRTL}>
                    <DetailLabel $isRTL={isRTL}>Conducteurs</DetailLabel>
                    <DetailValue $isRTL={isRTL}>{product.conducteurs}</DetailValue>
                  </DetailItem>
                  
                  <DetailItem $isRTL={isRTL}>
                    <DetailLabel $isRTL={isRTL}>Résistance</DetailLabel>
                    <DetailValue $isRTL={isRTL}>{product.resistance}</DetailValue>
                  </DetailItem>
                  
                  <DetailItem $isRTL={isRTL}>
                    <DetailLabel $isRTL={isRTL}>Chute de tension</DetailLabel>
                    <DetailValue $isRTL={isRTL}>{product.voltageDrop}</DetailValue>
                  </DetailItem>
                </ProductDetails>

                <ProductActions>
                  <PDFButton 
                    href={`/images/catalog/Câbles de transport et de distribution de l'énergie/Cable MT NFC 33-226 1220 (24) kV/produits/${product.filename}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <WhiteDownArrow /> PDF
                  </PDFButton>
                </ProductActions>
              </ProductCard>
            ))}
          </ProductsGrid>
        </ProductsSection>

        <CharacteristicsSection>
          <SectionTitle $isRTL={isRTL}>Caractéristiques techniques</SectionTitle>
          
          <CharacteristicItem $isRTL={isRTL}>
            <CharacteristicHeader 
              $isRTL={isRTL}
              onClick={() => toggleCharacteristic('electrical')}
            >
              <CharacteristicTitle $isRTL={isRTL}>
                Caractéristiques électriques
              </CharacteristicTitle>
              <ChevronIcon $isOpen={openCharacteristics.electrical}>
                ▼
              </ChevronIcon>
            </CharacteristicHeader>
            <CharacteristicContent $isOpen={openCharacteristics.electrical}>
              <CharacteristicGrid>
                <CharacteristicRow $isRTL={isRTL}>
                  <CharacteristicLabel $isRTL={isRTL}>Tension nominale</CharacteristicLabel>
                  <CharacteristicValue $isRTL={isRTL}>12/20 (24) kV</CharacteristicValue>
                </CharacteristicRow>
                <CharacteristicRow $isRTL={isRTL}>
                  <CharacteristicLabel $isRTL={isRTL}>Fréquence</CharacteristicLabel>
                  <CharacteristicValue $isRTL={isRTL}>50 Hz</CharacteristicValue>
                </CharacteristicRow>
                <CharacteristicRow $isRTL={isRTL}>
                  <CharacteristicLabel $isRTL={isRTL}>Courant de court-circuit</CharacteristicLabel>
                  <CharacteristicValue $isRTL={isRTL}>25 kA pendant 1 seconde</CharacteristicValue>
                </CharacteristicRow>
                <CharacteristicRow $isRTL={isRTL}>
                  <CharacteristicLabel $isRTL={isRTL}>Résistance d'isolement</CharacteristicLabel>
                  <CharacteristicValue $isRTL={isRTL}>≥ 1000 MΩ.km</CharacteristicValue>
                </CharacteristicRow>
              </CharacteristicGrid>
            </CharacteristicContent>
          </CharacteristicItem>

          <CharacteristicItem $isRTL={isRTL}>
            <CharacteristicHeader 
              $isRTL={isRTL}
              onClick={() => toggleCharacteristic('mechanical')}
            >
              <CharacteristicTitle $isRTL={isRTL}>
                Caractéristiques mécaniques
              </CharacteristicTitle>
              <ChevronIcon $isOpen={openCharacteristics.mechanical}>
                ▼
              </ChevronIcon>
            </CharacteristicHeader>
            <CharacteristicContent $isOpen={openCharacteristics.mechanical}>
              <CharacteristicGrid>
                <CharacteristicRow $isRTL={isRTL}>
                  <CharacteristicLabel $isRTL={isRTL}>Résistance mécanique aux chocs</CharacteristicLabel>
                  <CharacteristicValue $isRTL={isRTL}>Bonne</CharacteristicValue>
                </CharacteristicRow>
                <CharacteristicRow $isRTL={isRTL}>
                  <CharacteristicLabel $isRTL={isRTL}>Flexibilité du câble</CharacteristicLabel>
                  <CharacteristicValue $isRTL={isRTL}>Rigide</CharacteristicValue>
                </CharacteristicRow>
                <CharacteristicRow $isRTL={isRTL}>
                  <CharacteristicLabel $isRTL={isRTL}>Rayon de courbure minimum</CharacteristicLabel>
                  <CharacteristicValue $isRTL={isRTL}>15 × diamètre extérieur</CharacteristicValue>
                </CharacteristicRow>
              </CharacteristicGrid>
            </CharacteristicContent>
          </CharacteristicItem>

          <CharacteristicItem $isRTL={isRTL}>
            <CharacteristicHeader 
              $isRTL={isRTL}
              onClick={() => toggleCharacteristic('usage')}
            >
              <CharacteristicTitle $isRTL={isRTL}>
                Caractéristiques d'utilisation
              </CharacteristicTitle>
              <ChevronIcon $isOpen={openCharacteristics.usage}>
                ▼
              </ChevronIcon>
            </CharacteristicHeader>
            <CharacteristicContent $isOpen={openCharacteristics.usage}>
              <CharacteristicGrid>
                <CharacteristicRow $isRTL={isRTL}>
                  <CharacteristicLabel $isRTL={isRTL}>Non propagateur de la flamme</CharacteristicLabel>
                  <CharacteristicValue $isRTL={isRTL}>C2, NF C 32-070</CharacteristicValue>
                </CharacteristicRow>
                <CharacteristicRow $isRTL={isRTL}>
                  <CharacteristicLabel $isRTL={isRTL}>Résistance aux Intempéries</CharacteristicLabel>
                  <CharacteristicValue $isRTL={isRTL}>Excellente</CharacteristicValue>
                </CharacteristicRow>
                <CharacteristicRow $isRTL={isRTL}>
                  <CharacteristicLabel $isRTL={isRTL}>Température de service</CharacteristicLabel>
                  <CharacteristicValue $isRTL={isRTL}>-20°C à +90°C</CharacteristicValue>
                </CharacteristicRow>
                <CharacteristicRow $isRTL={isRTL}>
                  <CharacteristicLabel $isRTL={isRTL}>Température de court-circuit</CharacteristicLabel>
                  <CharacteristicValue $isRTL={isRTL}>250°C</CharacteristicValue>
                </CharacteristicRow>
              </CharacteristicGrid>
            </CharacteristicContent>
          </CharacteristicItem>
        </CharacteristicsSection>
      </Container>
    </ProductDetailPage>
  );
};

export default CableMT1220Detail;
